import axios from './axios-instance';
import ApiResponse from "../models/ApiResponse";
import SubscriptionPlan from "../models/SubscriptionPlan";
import {SubscriptionType} from "../models/enums/SubscriptionType";
import CheckoutSessionResponse from "../models/CheckoutSessionResponse";
import SubscriptionSummary from "../models/SubscriptionSummary";
import {getDateFromString} from "../utils/DateTimeUtils";

export const getSubscriptionPlan = async(id: number): Promise<ApiResponse<SubscriptionPlan>> => {
    try {
        const response = await axios.get('/subscription/' + id);
        return {
            data: response.data as SubscriptionPlan,
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to retrieve subscription plan', err.response?.data || err.message)
        return {
            data:  null,
            status: err.status
        };
    }
};

export const createStripeCheckout = async (email: string, subscriptionType: SubscriptionType):
    Promise<{clientSecret: string, status: number}> => {
    try {
        const body = {
            subscriptionType: subscriptionType,
            email: email
        };
        const response = await axios.post('/subscription/create-checkout', body);
        return {
            clientSecret: response.data,
            status: response.status
        }
    } catch (err: any) {
        console.error('Unable to create checkout session', err)
        return {
            status: err.status,
            clientSecret: ''
        }
    }
}

export const createStripePortalSession = async (userId: number): Promise<ApiResponse<string>> => {
    try {
        const response = await axios.post('/subscription/portal-session/' + userId);
        return {
            data: response.data,
            status: response.status
        }
    } catch (err: any) {
        console.error('Unable to create stripe portal session', err);
        return {
            data: null,
            status: err.status
        }
    }
}

export const getCheckoutSession = async(sessionId: string): Promise<ApiResponse<CheckoutSessionResponse>> => {
    try {
        const response = await axios.post('/subscription/checkout/session', null, {
            params: {
                sessionId: sessionId
            }
        });
        return {
            data: response.data as CheckoutSessionResponse,
            status: response.status
        }
    } catch (err: any) {
        console.error('Unable to retrieve checkout session', err);
        return {
            status: err.status,
            data: null
        }
    }
}

export const getSubscriptionSummary = async (userId: number): Promise<ApiResponse<SubscriptionSummary>> => {
    try {
        const response = await axios.get('/subscription/summary/' + userId);
        return {
            data: mapSubscriptionSummary(response.data),
            status: response.status
        }
    } catch (err: any) {
        console.error('Unable to retrieve subscription summary', err);
        return {
            data: null,
            status: err.status
        }
    }
}

export const cancelSubscription = async (userId: number): Promise<{status: number}> => {
    try {
        const response = await axios.post('/subscription/cancel/' + userId);
        return {status: response.status};
    } catch (err: any) {
        console.error('Unable to cancel subscription at this time', err);
        return {status: err.status};
    }
}

export const resumeSubscription = async (userId: number): Promise<{status: number}> => {
    try {
        const response = await axios.post('/subscription/resume/' + userId);
        return {status: response.status};
    } catch (err: any) {
        console.error('Unable to resume subscription at this time', err);
        return {status: err.status};
    }
}

const mapSubscriptionSummary = (data: any): SubscriptionSummary => {
    return {
        ...data,
        nextBillingDate: data.nextBillingDate ? getDateFromString(data.nextBillingDate) : null,
        cancellationDate: data.cancellationDate ? getDateFromString(data.cancellationDate) : null
    }
}